/**
 * MultiViewLobby Component - Optimized multi-section view
 * All sections visible in a scrollable list
 */

import { memo, useMemo, useRef, useState, useCallback } from 'react';
import type { FC } from 'react';

import { FavoritesSection, useFavoritesStore, useRevealFavoritesOnAdd } from 'features/favorites';
import { SECTION_LOADING } from 'features/navigation';
import {
  PRIZE_POP_CAMPAIGN_TYPE,
  REBATE_CAMPAIGN_TYPE,
  TOURNAMENTS_CAMPAIGN_TYPE,
  useCampaignsQuery,
  useCampaignSectionGate,
} from 'features/campaigns';

import { useOpenType } from '../../core/providers/OpenTypeContext';
import { useSections, useAppStore, useHasPrizePopSection } from '../../store/app/appStore';
import { useIsPortrait } from '../../shared/hooks/useIsPortrait';
import { useSettleScrollOnRotate } from '../../shared/hooks/useSettleScrollOnRotate';
import LoadingIndicator from '../../shared/components/LoadingIndicator';
import { useScrollRestoration } from '../../shared/hooks/useScrollRestoration';
import { consumeScrollAnchor, resolveAnchorPosition } from '../../shared/utils/scrollAnchor';
import LazySectionWrapper from '../../features/sections/components';

import styles from './Lobby.module.css';

interface MultiViewLobbyProps {
  hidden?: boolean;
}

const MultiViewLobby: FC<MultiViewLobbyProps> = ({ hidden }) => {
  const { sections } = useSections();
  const { settingsRequestDone } = useAppStore();
  const { openType } = useOpenType();
  const lobbyContainerRef = useRef<HTMLDivElement>(null);
  useSettleScrollOnRotate(lobbyContainerRef);

  // Read (and clear) the anchor left behind by the last game start - one shot per lobby boot
  const [scrollAnchor] = useState(consumeScrollAnchor);
  const { favorites } = useFavoritesStore();
  const isPortrait = useIsPortrait();

  const hasPrizePopSection = useHasPrizePopSection();

  useCampaignsQuery(TOURNAMENTS_CAMPAIGN_TYPE);
  useCampaignsQuery(PRIZE_POP_CAMPAIGN_TYPE, { enabled: hasPrizePopSection });
  useCampaignsQuery(REBATE_CAMPAIGN_TYPE);

  const { isCampaignEnabled } = useCampaignSectionGate();

  // Filter out campaign sections with no data or disabled by feature flag
  const visibleSections = useMemo(() => {
    if (!sections) return sections;
    return sections.filter(section => isCampaignEnabled(section.name) !== false);
  }, [sections, isCampaignEnabled]);

  // Determine if we should show the loader
  const isLoading = !settingsRequestDone || !visibleSections || visibleSections.length === 0;

  // ✅ Index of the section we are restoring to, so it can be preloaded
  const forcedPreloadIndex = useMemo(() => {
    if (!scrollAnchor || !visibleSections) return -1;
    return visibleSections.findIndex((section) => section.name === scrollAnchor.sectionName);
  }, [scrollAnchor, visibleSections]);

  // Resolved against the live DOM, so a section rendering at a different height than
  // when the anchor was taken can't leave us at the wrong offset
  const resolveScrollTarget = useCallback(() => {
    const container = lobbyContainerRef.current;
    if (!container || !scrollAnchor) return null;

    return resolveAnchorPosition(container, scrollAnchor);
  }, [scrollAnchor]);

  // Campaign sections join the list only once their query resolves, so re-apply the
  // target whenever the rendered set changes
  const renderedSectionsKey = useMemo(
    () => (visibleSections || []).map((section) => section.name).join('|'),
    [visibleSections]
  );

  useScrollRestoration({
    containerRef: lobbyContainerRef,
    resolveTarget: resolveScrollTarget,
    enabled: !!scrollAnchor && !isLoading,
    watchKey: renderedSectionsKey,
  });

  // Reveal the Favorites section on add when the user is at the bottom of the lobby.
  useRevealFavoritesOnAdd(lobbyContainerRef, favorites.games.length);

  // ✅ Always use Lazy Loading with Smart Preloading
  const sectionElements = useMemo(() => {
    if (!visibleSections || visibleSections.length === 0) return null;

    return visibleSections.map((section, index) => {
      // Smart preloading strategy:
      // 1. Always preload the first few sections (standard behavior)
      // 2. If restoring (or restored), preload the target section and its neighbors

      const isInitialSection = index < SECTION_LOADING.PRELOAD_COUNT;

      // Check if this section is near the restoration target
      // We use forcedPreloadIndex which persists after restoration
      const isNearTarget = forcedPreloadIndex !== -1 &&
        Math.abs(index - forcedPreloadIndex) <= 1;

      const shouldPreload = isInitialSection || isNearTarget;

      return (
        <LazySectionWrapper
          key={section.name}
          section={section}
          isPortrait={isPortrait}
          index={index}
          shouldPreload={shouldPreload}
        />
      );
    });
  }, [visibleSections, isPortrait, forcedPreloadIndex]);


  // Show loader while sections are loading
  if (isLoading) {
    return (
      <div
        ref={lobbyContainerRef}
        className={styles.lobbyContainer}
        data-view="multi"
        data-open-type={openType}
        data-section-container
        style={{
          ...(hidden ? { display: 'none' } : {}),
          ...(openType === 'ingame' ? { paddingTop: '40px' } : {}),
        }}
      >
        <LoadingIndicator show size="small" />
      </div>
    );
  }

  // Show all sections together when ready
  return (
    <div
      ref={lobbyContainerRef}
      className={styles.lobbyContainer}
      data-view="multi"
      data-open-type={openType}
      data-section-container
      style={hidden ? { display: 'none' } : undefined}
    >
      {sectionElements}
      <FavoritesSection
        data={favorites}
        isPortrait={isPortrait}
      />
    </div>
  );
};

// This component should rarely rerender
export default memo(MultiViewLobby);
